import React from "react";
import LegalPageShell from "./LegalPageShell";

const DEFAULT_BODY = `
<h2>1. Overview</h2>
<p>Because our house plans are <strong>digital, non-tangible, and instantly delivered</strong> once payment is confirmed, refunds are handled differently to physical goods. We want you to be delighted with your purchase, so we offer a fair, transparent policy that balances your protection with the reality that our plans are copyrighted intellectual property.</p>

<h2>2. Refund window &amp; conditions</h2>
<h3>2.1 Pre-download / pre-delivery cancellations</h3>
<p>If you have <strong>not yet downloaded</strong> the plan files and you request a refund <strong>within 24 hours</strong> of purchase, we will refund 100% of the purchase price, minus any non-refundable payment-processor fees.</p>

<h3>2.2 Post-download refunds</h3>
<p>Once you have downloaded the plan files (or accessed the download link), the digital delivery is considered complete and the plan is generally non-refundable. We may still consider a refund in the following cases:</p>
<ul>
  <li><strong>Duplicate purchase</strong> — you accidentally paid twice for the same plan.</li>
  <li><strong>Wrong plan</strong> — you purchased a different plan by mistake and have not shared the files with a third party. We may offer a plan-swap credit instead of a cash refund.</li>
  <li><strong>Materially inaccurate plan</strong> — you can demonstrate that the delivered plan differs substantially from the marketed specification (sq ft, bedroom count, primary elevation) in a way that could not reasonably be resolved by a minor revision.</li>
  <li><strong>Non-delivery</strong> — payment succeeded but files were not delivered and cannot be delivered within 5 business days.</li>
</ul>

<h3>2.3 Non-refundable items</h3>
<ul>
  <li>Custom-design deliverables that have progressed beyond the first-milestone approval.</li>
  <li>Plans downloaded and used to obtain a building permit or start construction.</li>
  <li>Any plan file that has been shared, published, or forwarded to a third party in violation of our <a href="/terms">Terms of Service</a>.</li>
</ul>

<h2>3. How to request a refund</h2>
<ol>
  <li>Email <a href="mailto:nagesh@dreamhomeengineers.com">nagesh@dreamhomeengineers.com</a> from the address on your order.</li>
  <li>Include your <strong>order number / DHE reference number</strong> and a brief description of the reason.</li>
  <li>Attach any supporting evidence (e.g. screenshots of the discrepancy or duplicate charges).</li>
  <li>We will respond within <strong>2 business days</strong> and, where refunds are approved, initiate the refund within <strong>5 business days</strong> of approval.</li>
</ol>

<h2>4. Refund methods &amp; timing</h2>
<ul>
  <li>Refunds are issued to the <strong>original payment method</strong>.</li>
  <li>Cards typically show the refund in <strong>5–10 business days</strong> depending on your bank.</li>
  <li>PayPal refunds are usually visible within <strong>1–3 business days</strong>.</li>
  <li>Razorpay UPI/net-banking refunds are typically settled in <strong>3–7 business days</strong>.</li>
  <li>Refunds are issued in the currency of your original payment; foreign-exchange differences are not compensated.</li>
</ul>

<h2>5. Custom Design engagements</h2>
<p>For paid custom-design engagements the refund policy is set out in your engagement letter and typically follows these principles:</p>
<ul>
  <li>Design deposits become <strong>non-refundable</strong> once the initial concept has been shared with you.</li>
  <li>Subsequent milestones become non-refundable once you approve them.</li>
  <li>If we materially fail to deliver a milestone by the agreed deadline (excluding delays caused by you), you may cancel the remaining work and receive a pro-rata refund of the unstarted portion.</li>
</ul>

<h2>6. Chargebacks &amp; disputes</h2>
<p>If you experience a payment issue we ask you to contact us <strong>before</strong> initiating a chargeback. Filing a chargeback without first requesting a refund from us may result in account suspension and legal recovery of the disputed amount. We share order and delivery evidence with payment processors as part of chargeback defence.</p>

<h2>7. Fraudulent orders</h2>
<p>We reserve the right to cancel and refund any order we reasonably believe to be fraudulent, and to report the transaction to the relevant authorities and payment processors.</p>

<h2>8. Changes to this policy</h2>
<p>We may update this Refund &amp; Cancellation Policy from time to time. The current version is always available at <a href="/refund">/refund</a>. Changes apply only to purchases made after the effective date shown at the top of this page.</p>

<h2>9. Contact</h2>
<p>Refund requests &amp; questions: <a href="mailto:nagesh@dreamhomeengineers.com">nagesh@dreamhomeengineers.com</a><br/>
Phone: +91 95508 99555 · Landline: +91 40 4850 2772</p>
`;

const Refund = () => (
  <LegalPageShell
    slug="refund"
    title="Refund & Cancellation Policy"
    effectiveDate="21 July 2026"
    summary="How refunds work at Dream Home Plans — including the digital-delivery window, non-refundable items, and how to submit a refund request."
    defaultBody={DEFAULT_BODY}
    contactEmail="nagesh@dreamhomeengineers.com"
  />
);

export default Refund;
